import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import { FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import countries from '../../../../core/consts/countries';

interface CountryOption {
  alpha2: string;
  name: string;
  emoji: string;
  callingCode: string;
}

interface Props {
  inputProps: any;
  onChange: (value: string) => void;
  onCountryCodeChange: (countryCode: string) => void;
  onBlur: any;
  helperText: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  error: boolean;
  countryCode: string;
}

function PhoneField(props: Props) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');

  const countryOptions = useMemo(() => {
    const seen = new Set<string>();
    const result: CountryOption[] = [];

    // Pin US first
    const us = countries.find((c) => c.alpha2 === 'US');
    if (us && us.countryCallingCodes.length > 0) {
      result.push({
        alpha2: us.alpha2,
        name: us.name,
        emoji: us.emoji,
        callingCode: us.countryCallingCodes[0],
      });
      seen.add(us.alpha2);
    }

    for (const c of countries) {
      if (!seen.has(c.alpha2) && c.emoji && c.countryCallingCodes.length > 0) {
        result.push({
          alpha2: c.alpha2,
          name: c.name,
          emoji: c.emoji,
          callingCode: c.countryCallingCodes[0],
        });
        seen.add(c.alpha2);
      }
    }
    return result;
  }, []);

  const selectedCountry = useMemo(() => {
    return (
      countryOptions.find((c) => c.callingCode === props.countryCode) ||
      countryOptions[0]
    );
  }, [props.countryCode, countryOptions]);

  const filteredCountries = useMemo(() => {
    if (!search) return countryOptions;
    const term = search.toLowerCase();
    return countryOptions.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.callingCode.includes(search)
    );
  }, [search, countryOptions]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/[^\d]/g, '');
    props.onChange(digits);
  };

  const handleCountrySelect = (country: CountryOption) => {
    props.onCountryCodeChange(country.callingCode.replace(/\s/g, ''));
    setAnchorEl(null);
    setSearch('');
  };

  const { defaultValue, value, ...restInputProps } = props.inputProps;
  return (
    <>
      <TextField
        color={'info'}
        error={props.error}
        autoFocus={true}
        value={value ?? ''}
        onChange={handleChange}
        onBlur={props.onBlur}
        fullWidth={true}
        label={t('phone')}
        variant="standard"
        margin={'dense'}
        helperText={props.helperText}
        InputLabelProps={{ shrink: true, error: false }}
        InputProps={{
          placeholder: t('phone'),
          type: 'tel',
          startAdornment: (
            <InputAdornment position="start">
              <ButtonBase
                onClick={(e) => setAnchorEl(e.currentTarget)}
                data-testid="country-selector"
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                {selectedCountry.emoji} {props.countryCode}
              </ButtonBase>
            </InputAdornment>
          ),
          ...restInputProps,
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSearch('');
        }}
        PaperProps={{ sx: { maxHeight: 300, width: 280 } }}
      >
        <ListSubheader sx={{ bgcolor: 'background.paper' }}>
          <TextField
            size="small"
            autoFocus
            placeholder={t('searchCountry')}
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            variant="standard"
            data-testid="country-search"
          />
        </ListSubheader>
        {filteredCountries.map((country) => (
          <MenuItem
            key={country.alpha2}
            onClick={() => handleCountrySelect(country)}
            selected={country.alpha2 === selectedCountry.alpha2}
            data-testid={`country-option-${country.alpha2}`}
          >
            {country.emoji} {country.name} ({country.callingCode})
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default PhoneField;
